import { Link } from "react-router-dom";
import {
  ArrowRightIcon,
  BarChart3Icon,
  CheckCircle2Icon,
  ChevronRightIcon,
  CircleDotIcon,
  FlagIcon,
  GaugeIcon,
  KeyRoundIcon,
  LayoutDashboardIcon,
  LockKeyholeIcon,
  MailCheckIcon,
  MedalIcon,
  RadioIcon,
  ShieldCheckIcon,
  SparklesIcon,
  TrophyIcon,
  UsersIcon,
} from "lucide-react";

const LOGO_URL =
  "https://yswafknmqpwjrsxjslbu.supabase.co/storage/v1/object/public/logos//logosmall.png";

const features = [
  {
    icon: LayoutDashboardIcon,
    eyebrow: "Planera",
    title: "Bygg tävlingen på ditt sätt",
    description:
      "Sätt upp deltagare, flera rundor, lag, handicap, spelformer och sidotävlingar i ett sammanhållet flöde.",
    points: [
      "Individuellt och lag",
      "Slagspel och HCP-justerat",
      "Tvåmannascramble",
    ],
  },
  {
    icon: RadioIcon,
    eyebrow: "Spela",
    title: "Live scorekort utan krångel",
    description:
      "Dela personliga scorekortslänkar och låt spelarna registrera resultat direkt ute på banan.",
    points: [
      "Hål för hål",
      "Mobilanpassat",
      "Löpande uppdateringar",
    ],
  },
  {
    icon: BarChart3Icon,
    eyebrow: "Följ",
    title: "Resultat som går att förstå",
    description:
      "Följ totalställning, rundresultat, lagtävling och sidotävlingar med tydliga brutto- och nettoresultat.",
    points: [
      "Live leaderboard",
      "Runddetaljer",
      "Brutto, netto och avdrag",
    ],
  },
];

const steps = [
  {
    number: "01",
    title: "Skapa tävlingen",
    description:
      "Välj spelare, rundor, hålantal, spelformer, lag och tävlingsregler.",
  },
  {
    number: "02",
    title: "Dela scorekorten",
    description:
      "Spelarna får en enkel länk och registrerar sina resultat direkt i mobilen.",
  },
  {
    number: "03",
    title: "Följ avgörandet",
    description:
      "Ställningen uppdateras löpande från första utslag till sista putt.",
  },
];

function Brand() {
  return (
    <Link to="/" className="inline-flex items-center gap-3">
      <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/15 bg-white/10 shadow-sm backdrop-blur-sm">
        <img
          src={LOGO_URL}
          alt="HoleInOne"
          className="h-7 w-7 object-contain brightness-125"
        />
      </span>

      <span>
        <span className="block text-[10px] font-semibold uppercase tracking-[0.3em] text-white/55">
          HoleInOne.se
        </span>
        <span className="mt-0.5 block text-sm font-medium text-white/85">
          Tournament manager
        </span>
      </span>
    </Link>
  );
}

function ProductPreview() {
  const players = [
    { position: 1, name: "Alexander", score: 214, movement: "−3" },
    { position: 2, name: "Johan", score: 216, movement: "−1" },
    { position: 3, name: "Marcus", score: 219, movement: "E" },
  ];

  return (
    <div className="relative mx-auto w-full max-w-[560px] lg:mx-0">
      <div className="absolute -left-8 top-16 hidden h-28 w-28 rounded-full bg-[#d4bc7b]/20 blur-3xl sm:block" />
      <div className="absolute -right-10 bottom-10 h-36 w-36 rounded-full bg-white/10 blur-3xl" />

      <div className="relative overflow-hidden rounded-[28px] border border-white/15 bg-[#f8f5ed] shadow-[0_28px_90px_rgba(7,24,17,0.38)]">
        <div className="flex items-center justify-between border-b border-[#dcd6c8] bg-[#fffdf8] px-4 py-3 sm:px-5">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#183d2d] text-white">
              <TrophyIcon className="h-4 w-4" />
            </span>
            <div>
              <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#9a7735]">
                Min tävling
              </p>
              <p className="text-sm font-semibold text-[#183d2d]">
                Live leaderboard
              </p>
            </div>
          </div>

          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#e8f1eb] px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.12em] text-[#287148]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#3f9c68]" />
            Live
          </span>
        </div>

        <div className="grid gap-3 p-3 sm:grid-cols-[1.25fr_0.75fr] sm:p-4">
          <div className="overflow-hidden rounded-2xl border border-[#ddd6c7] bg-white">
            <div className="grid grid-cols-[42px_1fr_62px] bg-[#183d2d] px-3 py-2.5 text-[8px] font-semibold uppercase tracking-[0.16em] text-white/65">
              <span>Plac.</span>
              <span>Spelare</span>
              <span className="text-right">Totalt</span>
            </div>

            <div className="divide-y divide-[#ece7dc]">
              {players.map((player) => (
                <div
                  key={player.name}
                  className="grid grid-cols-[42px_1fr_62px] items-center px-3 py-3"
                >
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#edf2ee] text-[11px] font-bold text-[#183d2d]">
                    {player.position}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-xs font-semibold text-[#26352e]">
                      {player.name}
                    </p>
                    <p className="mt-0.5 text-[9px] text-[#8a918c]">
                      {player.movement} senaste rundan
                    </p>
                  </div>
                  <p className="text-right text-sm font-bold tabular-nums text-[#183d2d]">
                    {player.score}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <div className="rounded-2xl border border-[#ddd6c7] bg-[#fffdf8] p-3.5">
              <div className="flex items-center justify-between">
                <span className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#8a918c]">
                  Aktiv runda
                </span>
                <FlagIcon className="h-3.5 w-3.5 text-[#9a7735]" />
              </div>
              <p className="mt-2 text-sm font-semibold text-[#183d2d]">
                Runda 3
              </p>
              <p className="mt-1 text-[10px] leading-4 text-[#68716c]">
                HCP-justerat slagspel · 18 hål
              </p>
              <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-[#e8e3d8]">
                <div className="h-full w-[72%] rounded-full bg-[#b08b45]" />
              </div>
              <p className="mt-1.5 text-[9px] text-[#8a918c]">
                13 av 18 hål registrerade
              </p>
            </div>

            <div className="rounded-2xl bg-[#183d2d] p-3.5 text-white">
              <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-white/45">
                Sidotävlingar
              </p>
              <div className="mt-3 space-y-2.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] text-white/70">CTP</span>
                  <span className="text-[10px] font-semibold text-[#dec98f]">
                    Alexander
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] text-white/70">
                    Longest drive
                  </span>
                  <span className="text-[10px] font-semibold text-[#dec98f]">
                    Marcus
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute -bottom-5 -left-3 hidden items-center gap-3 rounded-2xl border border-[#d9d1c0] bg-[#fffdf8] px-4 py-3 shadow-[0_14px_38px_rgba(12,32,23,0.18)] sm:flex">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#e7efe9] text-[#183d2d]">
          <RadioIcon className="h-4 w-4" />
        </span>
        <div>
          <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#9a7735]">
            Live scorekort
          </p>
          <p className="mt-0.5 text-xs font-semibold text-[#183d2d]">
            Uppdateras från banan
          </p>
        </div>
      </div>
    </div>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#f3f0e7] text-[#1f2d26]">
      <header className="relative overflow-hidden bg-[#112f23] text-white">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -right-32 -top-44 h-[520px] w-[520px] rounded-full bg-[#b08b45]/20 blur-3xl" />
          <div className="absolute -bottom-72 -left-36 h-[560px] w-[560px] rounded-full bg-white/[0.07] blur-3xl" />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.025)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.025)_1px,transparent_1px)] bg-[size:44px_44px] [mask-image:linear-gradient(to_bottom,black,transparent_88%)]" />
        </div>

        <nav className="relative mx-auto flex w-full max-w-7xl items-center justify-between px-4 py-5 sm:px-6 lg:px-8">
          <Brand />

          <div className="flex items-center gap-2 sm:gap-3">
            <Link
              to="/login"
              className="inline-flex shrink-0 items-center whitespace-nowrap rounded-xl px-3 py-2 text-xs font-semibold text-white/75 transition hover:bg-white/10 hover:text-white sm:px-4 sm:text-sm"
            >
              Logga in
            </Link>
            <Link
              to="/ansok-beta"
              className="hidden shrink-0 items-center gap-2 whitespace-nowrap rounded-xl bg-[#b08b45] px-4 py-2.5 text-sm font-semibold text-white shadow-[0_8px_24px_rgba(176,139,69,0.24)] transition hover:bg-[#be9a56] sm:inline-flex"
            >
              Ansök om åtkomst
              <ArrowRightIcon className="h-3.5 w-3.5" />
            </Link>
          </div>
        </nav>

        <div className="relative mx-auto grid w-full max-w-7xl gap-12 px-4 pb-20 pt-12 sm:px-6 sm:pb-24 sm:pt-16 lg:grid-cols-[0.92fr_1.08fr] lg:items-center lg:gap-16 lg:px-8 lg:pb-28 lg:pt-20">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#d8c38d]/25 bg-[#d8c38d]/10 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.19em] text-[#e2cf9d]">
              <SparklesIcon className="h-3.5 w-3.5" />
              Privat beta
            </div>

            <h1 className="mt-6 max-w-2xl text-4xl font-semibold tracking-[-0.035em] text-white sm:text-5xl lg:text-[58px] lg:leading-[1.08]">
              Allt för golftävlingen – från första utslag till sista putt.
            </h1>

            <p className="mt-6 max-w-xl text-base leading-7 text-white/62 sm:text-lg sm:leading-8">
              HoleInOne hjälper privata golfgäng att planera tävlingen,
              registrera resultat live och följa avgörandet i en tydlig,
              professionell vy.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/ansok-beta"
                className="group inline-flex items-center justify-center gap-2 rounded-xl bg-[#b08b45] px-5 py-3.5 text-sm font-semibold text-white shadow-[0_12px_30px_rgba(176,139,69,0.25)] transition hover:bg-[#be9a56]"
              >
                Ansök om betaåtkomst
                <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>

              <Link
                to="/register"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/[0.06] px-5 py-3.5 text-sm font-semibold text-white/85 backdrop-blur-sm transition hover:bg-white/10 hover:text-white"
              >
                <KeyRoundIcon className="h-4 w-4" />
                Jag har en åtkomstkod
              </Link>
            </div>

            <div className="mt-8 grid max-w-xl gap-3 text-xs text-white/58 sm:grid-cols-3">
              {[
                "Flera rundor",
                "Live scorekort",
                "Individuellt och lag",
              ].map((item) => (
                <div key={item} className="flex items-center gap-2">
                  <CheckCircle2Icon className="h-4 w-4 shrink-0 text-[#d5bd7e]" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <ProductPreview />
        </div>
      </header>

      <main>
        <section className="mx-auto w-full max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
          <div className="max-w-2xl">
            <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#9a7735]">
              En komplett tävlingsyta
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-[#183d2d] sm:text-4xl">
              Mindre administration. Mer tävling.
            </h2>
            <p className="mt-4 text-sm leading-7 text-[#68716c] sm:text-base">
              Från startfält och spelformer till live scorekort och slutlig
              leaderboard. HoleInOne håller ihop tävlingen utan kalkylblad,
              spridda meddelanden och manuella sammanställningar.
            </p>
          </div>

          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {features.map((feature) => {
              const Icon = feature.icon;

              return (
                <article
                  key={feature.title}
                  className="group rounded-[24px] border border-[#d8d2c4] bg-[#fffdf8] p-5 shadow-[0_10px_32px_rgba(31,45,38,0.055)] transition hover:-translate-y-0.5 hover:shadow-[0_18px_42px_rgba(31,45,38,0.09)] sm:p-6"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#e5ede7] text-[#183d2d] transition group-hover:bg-[#183d2d] group-hover:text-white">
                    <Icon className="h-5 w-5" />
                  </div>

                  <p className="mt-6 text-[9px] font-semibold uppercase tracking-[0.22em] text-[#9a7735]">
                    {feature.eyebrow}
                  </p>
                  <h3 className="mt-2 text-xl font-semibold text-[#183d2d]">
                    {feature.title}
                  </h3>
                  <p className="mt-3 text-sm leading-6 text-[#68716c]">
                    {feature.description}
                  </p>

                  <div className="mt-5 space-y-2.5 border-t border-[#e8e2d6] pt-5">
                    {feature.points.map((point) => (
                      <div
                        key={point}
                        className="flex items-center gap-2 text-xs font-medium text-[#435149]"
                      >
                        <CircleDotIcon className="h-3.5 w-3.5 text-[#b08b45]" />
                        {point}
                      </div>
                    ))}
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section className="border-y border-[#d9d2c4] bg-[#ebe7dd]">
          <div className="mx-auto grid w-full max-w-7xl gap-10 px-4 py-16 sm:px-6 sm:py-20 lg:grid-cols-[0.82fr_1.18fr] lg:items-center lg:px-8">
            <div>
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#183d2d] text-white shadow-sm">
                <GaugeIcon className="h-5 w-5" />
              </span>
              <p className="mt-6 text-[10px] font-semibold uppercase tracking-[0.25em] text-[#9a7735]">
                Så fungerar det
              </p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight text-[#183d2d] sm:text-4xl">
                Från upplägg till leaderboard i tre steg.
              </h2>
              <p className="mt-4 max-w-lg text-sm leading-7 text-[#68716c] sm:text-base">
                Tävlingsledaren sätter ramarna. Spelarna fokuserar på golfen.
                Resultaten samlas på samma plats.
              </p>
            </div>

            <div className="grid gap-3">
              {steps.map((step) => (
                <article
                  key={step.number}
                  className="grid grid-cols-[54px_1fr_auto] items-center gap-3 rounded-2xl border border-[#d6cfc0] bg-[#fffdf8] px-4 py-4 shadow-sm sm:grid-cols-[64px_1fr_auto] sm:px-5"
                >
                  <span className="text-lg font-semibold text-[#b08b45]">
                    {step.number}
                  </span>
                  <div>
                    <h3 className="text-sm font-semibold text-[#183d2d] sm:text-base">
                      {step.title}
                    </h3>
                    <p className="mt-1 text-xs leading-5 text-[#68716c] sm:text-sm">
                      {step.description}
                    </p>
                  </div>
                  <ChevronRightIcon className="h-4 w-4 text-[#a7aaa7]" />
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto grid w-full max-w-7xl gap-6 px-4 py-16 sm:px-6 sm:py-20 lg:grid-cols-2 lg:px-8 lg:py-24">
          <article className="relative overflow-hidden rounded-[28px] bg-[#183d2d] p-6 text-white shadow-[0_18px_52px_rgba(24,61,45,0.18)] sm:p-8">
            <div className="pointer-events-none absolute -right-24 -top-20 h-64 w-64 rounded-full bg-[#b08b45]/20 blur-3xl" />
            <div className="relative">
              <span className="inline-flex items-center gap-2 rounded-full border border-[#d8c38d]/20 bg-[#d8c38d]/10 px-3 py-1.5 text-[9px] font-semibold uppercase tracking-[0.18em] text-[#dfcc97]">
                <ShieldCheckIcon className="h-3.5 w-3.5" />
                Privat beta
              </span>

              <h2 className="mt-6 text-2xl font-semibold tracking-tight sm:text-3xl">
                Vi släpper in användare stegvis.
              </h2>
              <p className="mt-4 max-w-xl text-sm leading-7 text-white/62">
                Under betan söker vi golfgäng som vill använda tjänsten i
                riktiga tävlingar och hjälpa oss att göra flödena ännu bättre.
                Godkända ansökningar får en personlig åtkomstkod.
              </p>

              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4">
                  <MailCheckIcon className="h-5 w-5 text-[#d8c38d]" />
                  <p className="mt-3 text-sm font-semibold">1. Skicka ansökan</p>
                  <p className="mt-1 text-xs leading-5 text-white/50">
                    Berätta hur du vill använda HoleInOne.
                  </p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4">
                  <LockKeyholeIcon className="h-5 w-5 text-[#d8c38d]" />
                  <p className="mt-3 text-sm font-semibold">2. Få en kod</p>
                  <p className="mt-1 text-xs leading-5 text-white/50">
                    Koden används en gång när kontot skapas.
                  </p>
                </div>
              </div>

              <Link
                to="/ansok-beta"
                className="group mt-7 inline-flex items-center gap-2 rounded-xl bg-[#b08b45] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#be9a56]"
              >
                Ansök om åtkomst
                <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </article>

          <article className="rounded-[28px] border border-[#d8d2c4] bg-[#fffdf8] p-6 shadow-[0_12px_38px_rgba(31,45,38,0.06)] sm:p-8">
            <div className="flex items-start justify-between gap-4">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#e5ede7] text-[#183d2d]">
                <UsersIcon className="h-5 w-5" />
              </span>
              <span className="rounded-full border border-[#dfd6c4] bg-[#f4efe4] px-3 py-1 text-[9px] font-semibold uppercase tracking-[0.16em] text-[#9a7735]">
                Kommer senare
              </span>
            </div>

            <p className="mt-6 text-[10px] font-semibold uppercase tracking-[0.23em] text-[#9a7735]">
              För golfklubbar
            </p>
            <h2 className="mt-3 text-2xl font-semibold tracking-tight text-[#183d2d] sm:text-3xl">
              En separat klubbportal är nästa steg.
            </h2>
            <p className="mt-4 text-sm leading-7 text-[#68716c]">
              Vi förbereder en egen ingång för golfklubbar med andra behov,
              behörigheter och arbetsflöden än privatkonton. Klubbar kan redan
              nu lämna en intresseanmälan.
            </p>

            <Link
              to="/golfklubb"
              className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-[#183d2d] transition hover:text-[#9a7735]"
            >
              Läs om klubbspåret
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </article>
        </section>

        <section className="px-4 pb-16 sm:px-6 sm:pb-20 lg:px-8 lg:pb-24">
          <div className="mx-auto flex w-full max-w-7xl flex-col items-start justify-between gap-6 rounded-[28px] border border-[#d8d2c4] bg-[#fffdf8] p-6 shadow-[0_14px_44px_rgba(31,45,38,0.07)] sm:flex-row sm:items-center sm:p-8">
            <div>
              <div className="flex items-center gap-2 text-[#9a7735]">
                <MedalIcon className="h-4 w-4" />
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em]">
                  Redo för nästa tävling?
                </p>
              </div>
              <h2 className="mt-3 text-2xl font-semibold text-[#183d2d]">
                Ansök till betan eller logga in på ditt konto.
              </h2>
            </div>

            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
              <Link
                to="/login"
                className="inline-flex items-center justify-center rounded-xl border border-[#cfc7b8] bg-white px-5 py-3 text-sm font-semibold text-[#183d2d] transition hover:bg-[#f5f1e8]"
              >
                Logga in
              </Link>
              <Link
                to="/ansok-beta"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#183d2d] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#214d39]"
              >
                Ansök om åtkomst
                <ArrowRightIcon className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-[#d9d2c4] bg-[#ebe7dd]">
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-4 px-4 py-6 text-xs text-[#68716c] sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
          <div className="flex items-center gap-2.5">
            <img
              src={LOGO_URL}
              alt=""
              className="h-6 w-6 object-contain"
            />
            <span>
              © {new Date().getFullYear()} HoleInOne.se · En tjänst från
              Hollander Web
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <Link to="/login" className="transition hover:text-[#183d2d]">
              Logga in
            </Link>
            <Link
              to="/ansok-beta"
              className="transition hover:text-[#183d2d]"
            >
              Betaansökan
            </Link>
            <Link
              to="/golfklubb"
              className="transition hover:text-[#183d2d]"
            >
              Golfklubbar
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
